import type { Tutorial } from '../types'

const IMG = '/images/tutorials/web/accountability-partner-workflow'

export const accountabilityPartnerWorkflowWebTutorial: Tutorial = {
  slug: 'accountability-partner-workflow',
  platform: 'web',
  title: 'Goals Partner workflow',
  subtitle: 'End-to-end on web — invite, support, optional paid tier',
  date: '2026-07-09',
  category: 'Sharing & Support',
  tags: ['Goals Partners', 'workflow', 'Stripe', 'Coach brief'],
  description:
    'Full desktop walkthrough of the Goals Partner loop: a goal owner invites a partner, both share a feed, the partner gets Coach briefs and attention signals, and community advisors can list paid packages.',
  highlights: [
    'Goal owner hub at /partnerships — invite, feed, and scope management.',
    'Partner attention panel surfaces missed check-ins and streak wins.',
    'Optional paid tier: verified Partner Profile, Stripe Connect, and Checkout booking.',
  ],
  coverImage: `${IMG}/01-client-partnerships-hub.png`,
  sections: [
    {
      heading: 'Step 1 — Goal owner opens Goals Partners',
      paragraphs: [
        'Navigate to **Goals Partners** from the user menu or `/partnerships`. This is the hub for every partnership: active, pending, and ended.',
      ],
      image: {
        src: `${IMG}/01-client-partnerships-hub.png`,
        alt: 'Client partnerships hub on web',
        caption: 'Client partnerships hub',
      },
    },
    {
      heading: 'Step 2 — Goal owner sends an invite',
      paragraphs: [
        'Click **Invite a partner**, enter their email, select goals to share (progress and achievements only), and send. The backend emails a link to accept the partnership.',
      ],
      image: {
        src: `${IMG}/02-client-invite-dialog.png`,
        alt: 'Invite partner drawer on web',
        caption: 'Invite drawer',
      },
    },
    {
      heading: 'Step 3 — Partner accepts',
      paragraphs: [
        'The partner signs in, opens the accept link, accepts the Goals Partner waiver if prompted, and confirms. Status moves to **active**. Scoped goals become visible on the partner goals dashboard only — never unshared records or genetics.',
      ],
    },
    {
      heading: 'Step 4 — Goal owner sees the shared feed',
      paragraphs: [
        'Both parties share one activity feed per partnership. The goal owner sees partner comments and system **missed check-in** reminders. Owners get a **Log a check-in** button on missed-check-in cards.',
      ],
      image: {
        src: `${IMG}/03-client-partnership-feed.png`,
        alt: 'Client partnership feed on web',
        caption: 'Client partnership feed',
      },
    },
    {
      heading: 'Step 5 — Partner attention hub',
      paragraphs: [
        'When the partner opens `/partnerships`, **Where your attention may help** surfaces structured signals: missed check-ins, streak wins, lapsed goals. Each row offers **Leave a comment** so the partner writes their own supportive message.',
      ],
      image: {
        src: `${IMG}/04-partner-attention-hub.png`,
        alt: 'Partner attention hub on web',
        caption: 'Partner attention hub',
      },
    },
    {
      heading: 'Step 6 — Partner goals + Wellness Coach brief',
      paragraphs: [
        'On `/partnerships/:id/goals`, the partner sees scoped goal cards plus the **How to support this goal owner** coach brief — generated from shared goal context, not genetics or records.',
      ],
      image: {
        src: `${IMG}/05-partner-goals-coach-brief.png`,
        alt: 'Partner goals and coach brief on web',
        caption: 'Partner goals and coach brief',
      },
    },
    {
      heading: 'Step 7 — Partner feed (same thread)',
      paragraphs: [
        'The partner view of the feed matches the goal owner\'s timeline with actor attribution. Goal-anchored comments show the target goal name.',
      ],
      image: {
        src: `${IMG}/06-partner-partnership-feed.png`,
        alt: 'Partner partnership feed on web',
        caption: 'Partner partnership feed',
      },
    },
    {
      heading: 'Step 8 — Optional: paid AP profile listing',
      paragraphs: [
        'Community advisors can list packages at **My AP profiles** (`/partners/my-listing`): username, packages, required data access, capacity.',
      ],
      image: {
        src: `${IMG}/07-partner-ap-profile-listing.png`,
        alt: 'AP profile listing entry point on web',
        caption: 'AP profile listing',
      },
    },
    {
      heading: 'Step 9 — Admin verification',
      paragraphs: [
        'New paid profiles start **pending** until an admin approves them. After approval the profile shows **Verified** on My AP profiles. (Staging screenshots for this step are captured separately.)',
      ],
    },
    {
      heading: 'Step 10 — Stripe Connect: affiliate tab',
      paragraphs: [
        'Partners with incomplete Stripe onboarding see **Complete your Stripe onboarding** on Account → **Affiliate**. **Complete setup** resumes the hosted Express flow.',
      ],
    },
    {
      heading: 'Step 11 — Stripe Connect: hosted onboarding',
      paragraphs: [
        'The partner completes identity and payout details on Stripe\'s hosted pages. In production, `payouts_enabled` syncs from Stripe webhooks after real onboarding.',
      ],
    },
    {
      heading: 'Step 12 — Book package → Stripe Checkout',
      paragraphs: [
        'On a public, verified profile (`/ap/:username`), the goal owner clicks **Book this package**, confirms scope, and submits. The API returns a Stripe Checkout URL.',
      ],
    },
  ],
  whyMatters:
    'This is the full accountability loop on web — from a free goal-scoped invite through optional paid community support with Stripe. Every step is captured from the real product, not mockups.',
  relatedSlugs: ['collaborator-messaging', 'profile-sharing'],
  meta: {
    description:
      'End-to-end Goals Partner workflow on Livewello web — invite, feed, Coach brief, and optional paid tier with Stripe.',
    ogImage: `${IMG}/01-client-partnerships-hub.png`,
  },
}
